import { useTranslation } from "react-i18next";
import PersonalShell, { usePersonalProse } from "./PersonalShell";
import { FOCUS } from "@/pages/home/ui";

// /personal/delete-account: the web page the App Store and Google Play ask for, where people can request deletion of
// their Photohawk Family app account without the app (public in production from 20 October 2026). In the app it's Sharing and privacy > Delete account
// (photohawk-personal), which calls DELETE /personal/account (photohawk-serverless src/stacks/personal `deleteAccount`):
// that deletes, at once, the chats the user started (for everyone), their messages and photos in other chats (and
// they leave; others' messages stay), shares, invites, shared and undelivered encrypted photos, devices, being-found
// entries, entitlement cache, profile and waitlist entry, the cloud face collection, print files and order addresses,
// and the Photohawk account itself unless Photohawk Studio still uses it. The app then erases the face data on the
// phone (photohawk-personal PrivacyController.eraseAfterAccountDeletion).

export const SUPPORT_EMAIL = "support@photohawk.com";

export const deletionMailto = (subject: string, body: string) =>
  `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const DeleteAccountPage = () => {
  const { t } = useTranslation();
  const d = (key: string) => t(`home.deleteAccount.${key}`);
  const prose = usePersonalProse();
  return (
    <PersonalShell title={`${d("title")} | Photohawk`}>
      <article className={prose} data-testid="delete-account">
        <h1>{d("title")}</h1>
        <p>{d("intro")}</p>

        <h2>{d("inAppHeading")}</h2>
        <ol>
          <li>{d("step1")}</li>
          <li>{d("step2")}</li>
          <li>{d("step3")}</li>
        </ol>

        <h2>{d("deletedHeading")}</h2>
        <ul>
          <li>{d("deleted1")}</li>
          <li>{d("deleted2")}</li>
          <li>{d("deleted3")}</li>
          <li>{d("deleted4")}</li>
          <li>{d("deleted5")}</li>
        </ul>
        <p>{d("onPhone")}</p>

        <h2>{d("keptHeading")}</h2>
        <ul>
          <li>{d("kept1")}</li>
          <li>{d("kept2")}</li>
        </ul>
        <p>
          <strong>{d("subscription")}</strong>
        </p>

        <h2 id="email">{d("emailHeading")}</h2>
        <p>{d("emailText")}</p>
        <p>
          <a
            href={deletionMailto(d("emailSubject"), d("emailBody"))}
            data-testid="delete-email"
            className={`!inline-flex h-11 items-center rounded-xl bg-primary px-6 text-[15px] !font-semibold !text-primary-foreground !no-underline shadow-md transition-all hover:bg-primary/90 hover:shadow-lg ${FOCUS}`}
          >
            {d("emailButton")}
          </a>
        </p>
        <p className="text-sm text-muted-foreground">
          {d("emailFallback")} <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </article>
    </PersonalShell>
  );
};

export default DeleteAccountPage;
